import { computed, ref, Ref } from 'vue';
import { notification } from 'ant-design-vue';
import store from '@/store';
import i18n from '@/i18n';
import { CombinationsOptions, JOINTS_OPTIONS } from '@/enums/global/GlobalOptions';
import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { getPositionDropdownOptions } from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';

/**
 * Owns the joints-configurator table state and all handlers that mutate it:
 * - building rows from store values (`renderFormData`)
 * - toggling row selection with the 2-position cap
 * - updating individual field values (assembly position dropdown)
 * - keeping the assembly-position dropdown options in sync with the selection
 * - wiping assembly-position values when the user navigates back from step 2
 */
export function useJointsTableData(
    productData: Ref<ProductRowItem[]>,
    maxSelectedPositions: Ref<number>,
    currentConfig: any,
) {
    const tableHeaderFields = computed(
        () => currentConfig.value?.checklistFields.filter((field: any) => field.segment === 'table') || [],
    );
    const checklistFields = computed(() => currentConfig.value?.checklistFields || []);
    const productRowsFormData = ref<CombinationsConfiguratorFormData[]>([]);
    const productsSelectedForJoint = ref<number[]>([]);

    const assemblyPositionFieldPID = String(JOINTS_OPTIONS.JOINTS_POSITION_PID);

    function getProductFormValues(productFormId: number): ActiveProductFormValueObject | null {
        return store.getters['configurator/activeProductForm'](productFormId);
    }

    // ------------------------------------------------------------------------
    // Completion flags
    // ------------------------------------------------------------------------
    const isPositionSelectionCompleted = computed<boolean>(
        () => productsSelectedForJoint.value.length === maxSelectedPositions.value,
    );

    const isPositionOrderCompleted = computed<boolean>(() => {
        if (!isPositionSelectionCompleted.value) {
            return false;
        }

        const assignedPositions = productsSelectedForJoint.value.map((selectedProductId) => {
            const selectedRow = productRowsFormData.value.find((row) => row.activeProductFormId === selectedProductId);
            return selectedRow?.formData.find((field) => field.fieldPID === assemblyPositionFieldPID)?.fieldValue;
        });

        const everyPositionAssigned = assignedPositions.every((value) => value !== '' && value != null);
        const allAssignedPositionsAreUnique = new Set(assignedPositions).size === assignedPositions.length;

        return everyPositionAssigned && allAssignedPositionsAreUnique;
    });

    // ------------------------------------------------------------------------
    // Mutations
    // ------------------------------------------------------------------------
    function toggleProductSelection(productId: number) {
        const indexInSelection = productsSelectedForJoint.value.indexOf(productId);

        if (indexInSelection === -1) {
            if (productsSelectedForJoint.value.length >= maxSelectedPositions.value) {
                notification.warn({
                    message: i18n.t('Maximum positions selected') as string,
                    description: i18n.t('You can only select up to two positions for a corner joint.') as string,
                });
                return;
            }

            productsSelectedForJoint.value.push(productId);
        } else {
            productsSelectedForJoint.value.splice(indexInSelection, 1);
        }

        adjustAssemblyPositionOptions();
    }

    function onFormFieldChange({
        productId,
        fieldPID,
        value,
    }: {
        productId: number;
        fieldPID: string;
        value: string | number | boolean;
    }) {
        const rowIndex = productRowsFormData.value.findIndex((row) => row.activeProductFormId === productId);
        if (rowIndex === -1) {
            return;
        }

        const fieldIndex = productRowsFormData.value[rowIndex].formData.findIndex(
            (field) => field.fieldPID === fieldPID,
        );
        if (fieldIndex !== -1) {
            productRowsFormData.value[rowIndex].formData[fieldIndex].fieldValue = value;
        }
    }

    /** Keeps the assembly-position dropdown options in sync with the selection size. */
    function adjustAssemblyPositionOptions() {
        const dropdownOptions = getPositionDropdownOptions(productsSelectedForJoint.value.length);

        productRowsFormData.value.forEach((row) => {
            const assemblyPositionField = row.formData.find((field) => field.fieldPID === assemblyPositionFieldPID);
            if (assemblyPositionField) {
                assemblyPositionField.options = dropdownOptions;
            }
        });
    }

    /** Wipes every row's assembly-position value (used when leaving step 2 backwards). */
    function clearAssemblyPositionValues() {
        productRowsFormData.value.forEach((row) => {
            const assemblyPositionField = row.formData.find((field) => field.fieldPID === assemblyPositionFieldPID);
            if (assemblyPositionField) {
                assemblyPositionField.fieldValue = '';
            }
        });
    }

    // ------------------------------------------------------------------------
    // Initialisation
    // ------------------------------------------------------------------------
    function renderFormData() {
        productRowsFormData.value = productData.value.map((product) => {
            const activeProductFormValues = getProductFormValues(product.activeProductFormId);
            const jointProductRow: CombinationsConfiguratorFormData = {
                activeProductFormId: product.activeProductFormId,
                isHighlighted: product.connection !== null,
                formData: [],
            };

            checklistFields.value.forEach((field: any) => {
                const storedFieldValue = activeProductFormValues?.[field.pid as string];
                const defaultValueFromConfig = field.value || storedFieldValue || '';
                const initialFieldValue = field.isReadOnly ? defaultValueFromConfig : '';

                jointProductRow.formData.push({
                    fieldPID: String(field.pid),
                    fieldLabel: field.label,
                    fieldValue: initialFieldValue === 'Nedefinirano' ? '' : initialFieldValue,
                    shouldUpdateConfigurator: field.shouldUpdateConfigurator,
                    fieldType: field.type,
                    isReadOnly: field.isReadOnly || false,
                    segment: field.segment,
                    options: field?.options || [],
                    step: field?.step || 1,
                    isNarrow: field?.isNarrow || false,
                    key: field?.key || String(field.pid),
                    defaultValue: field?.defaultValue || '',
                });
            });

            return jointProductRow;
        });

        adjustAssemblyPositionOptions();
    }

    function resetTableData() {
        productRowsFormData.value = [];
        productsSelectedForJoint.value = [];
    }

    return {
        // State
        tableHeaderFields,
        productRowsFormData,
        productsSelectedForJoint,
        // Completion
        isPositionSelectionCompleted,
        isPositionOrderCompleted,
        // Handlers
        toggleProductSelection,
        onFormFieldChange,
        adjustAssemblyPositionOptions,
        clearAssemblyPositionValues,
        renderFormData,
        resetTableData,
    };
}
